/**
 * OSR leads — labels, formatters, and the one way the page talks to the
 * server. The lists mirror chariot-backend/services/osr/leadShared.js; the
 * server also sends them (GET /admin/osr/meta), these are what the page
 * shows before that answer arrives.
 */

import { fetchWithAuth } from "../../utils/api";

// ── Talking to the server ──────────────────────────────────────────────────
// Every call resolves to the JSON body or throws an Error carrying the
// server's sentence (`message`), its `status`, and its `code`, so callers
// branch on a code and show the sentence. They never parse text.
export async function osrFetch(path, { method = "GET", body, skipSaveIndicator } = {}) {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  let res;
  try {
    res = await fetchWithAuth(`/admin/osr${path}`, {
      method,
      skipSaveIndicator,
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch (cause) {
    // No answer at all (offline, DNS, a dropped connection): a sentence, not
    // the browser's "Failed to fetch".
    const err = new Error("Couldn't reach Chariot. Check your connection and try again.");
    err.status = 0;
    err.code = "network";
    err.data = {};
    err.cause = cause;
    throw err;
  }
  let json = null;
  try {
    json = await res.json();
  } catch {
    /* an empty or non-JSON body is handled below */
  }
  if (!res.ok || !json || json.success === false) {
    const err = new Error(res.status === 504
      ? "That took too long. Check your connection and try again."
      : (json && json.error) || "Something went wrong. Please try again.");
    err.status = res.status;
    err.code = json && json.code;
    err.data = json || {};
    throw err;
  }
  return json;
}

// ── Outcomes ───────────────────────────────────────────────────────────────
// Estimate counts against conversion without being called a loss.
export const OUTCOMES = [
  { value: "pending", label: "Pending", family: "yellow", hint: "No decision yet" },
  { value: "estimate", label: "Estimate", family: "blue", hint: "A price is with the customer" },
  { value: "sold", label: "Sold", family: "green", hint: "The sale is written up" },
  { value: "lost", label: "Lost", family: "red", hint: "Not going ahead" },
];
// Not a sale: sent to Customer Service as a Resolution Center ticket (chariot-backend services/osr/leadToCs.js). Only
// "Send to Customer Service" sets it, never an Outcome button; it shows under Closed and the lists filter by it.
export const CS_OUTCOME = { value: "customer_service", label: "Customer service", family: "gray", hint: "Not a sale: sent to Customer Service as a Resolution Center ticket" };
export const LIST_OUTCOMES = [...OUTCOMES, CS_OUTCOME];
export const OUTCOME_BY_VALUE = Object.fromEntries(LIST_OUTCOMES.map((o) => [o.value, o]));

/**
 * The one word the Status column says about a lead, with its color family.
 * Spam takes two people: a flag ("Flagged") waits on a manager, a confirmed
 * mark is "Spam".
 */
export function statusOf(lead) {
  if (!lead) return { label: "—", family: "gray" };
  if (lead.is_spam) return { label: "Spam", family: "purple", hint: lead.spam_reason || "Marked as spam" };
  if (lead.spam_status === "suspected") return { label: "Flagged", family: "purple", sub: "as spam · awaiting review", hint: "Flagged as spam. It stays here until a manager confirms or rejects the flag." };
  if (lead.status === "uncaptured") return { label: "New", family: "orange", hint: "Nobody has claimed it yet" };
  const o = OUTCOME_BY_VALUE[lead.outcome] || OUTCOME_BY_VALUE.pending;
  return { label: o.label, family: o.family, hint: o.hint };
}

/** Where a call opens in Sales Intake. */
export const callLink = (callId) => `/app/sales-intake/history?call=${encodeURIComponent(callId)}`;

// endsTexts: on a booking still waiting on its card, only these also mark the booking Lost and stop its follow-up
// texts (NS Follow-Up); any other reason leaves both going (chariot-backend services/osr/nsBookingCards.js lostEndsBooking).
export const LOST_REASONS = [
  { code: "waiting_on_approval", label: "Waiting on Approval" },
  { code: "price_shopping", label: "Price Shopping" },
  { code: "out_of_area", label: "Out of Area", endsTexts: true },
  { code: "no_answer", label: "No Answer / Unable to Reach" },
  { code: "customer_deferred", label: "Customer Deferred" },
  { code: "competitor", label: "Competitor" },
  { code: "parts_availability", label: "Parts Availability" },
  { code: "other", label: "Other / unlisted", endsTexts: true },
];

export const SERVICE_CATEGORIES = [
  { code: "engine", label: "Engine / mechanical repair" },
  { code: "maintenance", label: "Routine maintenance" },
  { code: "electrical", label: "Electrical / electronics" },
  { code: "fuel", label: "Fuel system" },
  { code: "detailing", label: "Detailing / fiberglass" },
  { code: "trailer", label: "Trailer" },
  { code: "repower", label: "Repower / engine sales" },
  { code: "inspection", label: "Inspection / diagnostic" },
  { code: "membership", label: "Membership / billing" },
  { code: "other", label: "Other" },
  { code: "not_service", label: "Not a service request" },
];
export const CATEGORY_LABELS = Object.fromEntries(SERVICE_CATEGORIES.map((c) => [c.code, c.label]));

export const CONTACT_CHANNELS = [
  { value: "call", label: "Phone call" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In person" },
];

// "" is each view's own order; DEFAULT_ORDER says what that is.
export const SORT_OPTIONS = [
  { value: "", label: "Default order" },
  { value: "newest", label: "Arrival — Newest first" },
  { value: "oldest", label: "Arrival — Oldest first" },
  { value: "claimed", label: "Recently claimed" },
  { value: "follow_up", label: "Follow-up date" },
  { value: "decided", label: "Recently decided" },
];

export const DEFAULT_ORDER = {
  working: "Emergencies first, then leads not called yet, then follow-ups that are due.",
  followups: "The most overdue first.",
  closed: "The most recently decided first.",
  spam: "The newest first.",
};

export const MARKET_SOURCE_HINT = {
  manual: "Set by a person",
  customer: "From the customer's home market",
  address: "From the address",
  intake: "From the location on the request",
  line: "From the number they called",
  area_code: "From the phone's area code",
};

// ── Sources ────────────────────────────────────────────────────────────────
const SOURCE_LABELS = {
  sms_image: "Texted photo",
  sms_text: "Texted message",
  Staff: "Entered by staff",
  "Field rep": "Field rep",
  wpforms: "Website form",
  "AI receptionist": "AI receptionist (phone)",
  "Website chat": "Website chat (Hank)",
  // A booking with no card yet, sent to OSR for the card (chariot-backend services/osr/nsBookingLeads.js).
  "AI receptionist (no card)": "Booked with Hank · needs a card",
  "Online booking (no card)": "Booked online · needs a card",
};
/** "WPForms Service Appointment 1" → "Service Appointment". */
export function sourceLabel(source) {
  if (!source) return "Unknown source";
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source];
  return String(source)
    .replace(/^WPForms\s+/i, "")
    .replace(/\s+\d+(\s+Email Link)?$/i, (m, link) => (link ? " (email link)" : ""))
    .replace(/\s+Form$/i, "")
    .trim() || source;
}

/** Who handed a lead in: a name, else the number it was texted from. */
export function handedInBy(lead) {
  if (!lead) return null;
  if (lead.submitted_by_name) return lead.submitted_by_name;
  if (lead.submitted_by_phone) return formatPhone(lead.submitted_by_phone);
  return null;
}

// ── Formatters ─────────────────────────────────────────────────────────────
export function formatPhone(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  const ten = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  if (ten.length !== 10) return phone || "";
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export const telHref = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.length === 10) return `tel:+1${d}`;
  if (d.length === 11 && d[0] === "1") return `tel:+${d}`;
  return null;
};

/** A length of time in words a person would say: "45s", "12 min", "3h 20m", "2 days". */
export function fmtSpan(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(s / 86400);
  return `${d} day${d === 1 ? "" : "s"}`;
}

export const secondsSince = (iso, now = Date.now()) => (iso ? Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000)) : null);

/** "2 min ago", "3h ago", "Tue 4:15 PM", "Sep 12". */
export function fmtWhen(iso, now = Date.now()) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const s = Math.floor((now - d.getTime()) / 1000);
  if (s >= 0 && s < 60) return "just now";
  if (s >= 0 && s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s >= 0 && s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const opts = { timeZone: "America/New_York" };
  if (Math.abs(s) < 6 * 86400) return d.toLocaleString("en-US", { ...opts, weekday: "short", hour: "numeric", minute: "2-digit" });
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString("en-US", { ...opts, month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

/** "2:14 PM", Eastern. */
export function fmtClock(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** A date with no time ("2026-10-08") shown without shifting a day across time zones. */
export function fmtDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ""));
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export const fmtMoney = (cents) =>
  cents == null ? "" : `$${(Number(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const fmtPct = (pct) => (pct == null ? "—" : `${pct}%`);

/** How long a new lead has waited, and how worried to look about it. */
export function waitingLevel(seconds) {
  if (seconds == null) return "low";
  if (seconds > 3600) return "high";
  if (seconds > 900) return "med";
  return "low";
}

/** For <input type="datetime-local">, in the viewer's own time zone. */
export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export const fromLocalInput = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** What the "Next step" column says about a lead. */
export function nextStep(lead, now = Date.now()) {
  if (!lead) return { text: "—", tone: "plain" };
  if (!lead.is_spam && lead.spam_status === "suspected") {
    return { text: "Waiting on a manager", tone: "warn", sub: `flagged as spam ${fmtWhen(lead.spam_flagged_at, now)}` };
  }
  if (lead.outcome === "sold") {
    // A booking's lead its card marked Sold (before Oct 28 2026): the booking's Sales Sheet goes from Sales Intake.
    if (lead.booking_lead && !lead.sales_submission_id) return { text: "Card on file", tone: "good", sub: fmtWhen(lead.outcome_set_at, now) };
    return { text: lead.sales_submission_id ? "Sales Sheet sent" : "Sold, no Sales Sheet yet", tone: lead.sales_submission_id ? "good" : "warn", sub: fmtWhen(lead.outcome_set_at, now) };
  }
  if (lead.outcome === "lost") return { text: lead.lost_reason_label || "Lost", tone: "plain", sub: fmtWhen(lead.outcome_set_at, now) };
  // Not a sale: a Resolution Center ticket now, nothing more for sales to do.
  if (lead.outcome === "customer_service") return { text: "With Customer Service", tone: "plain", sub: fmtWhen(lead.outcome_set_at, now) };
  // A booking's card is in: its writer sends the booking's Sales Sheet from the lead (chariot-backend services/osr/leadSubmit.js).
  if (lead.sheet_to_send) return { text: "Send the Sales Sheet", tone: "warn", sub: "card on file" };
  if (lead.next_follow_up_at) {
    const due = new Date(lead.next_follow_up_at).getTime();
    const overdue = due < now;
    return { text: overdue ? "Follow-up overdue" : "Follow up", tone: overdue ? "bad" : "plain", sub: fmtDateTime(lead.next_follow_up_at) };
  }
  if (!lead.first_contact_at) return { text: "Not called yet", tone: "warn", sub: lead.captured_at ? `claimed ${fmtWhen(lead.captured_at, now)}` : "" };
  const n = Number(lead.contact_attempts) || 0;
  return { text: `Called ${n === 1 ? "once" : `${n} times`}`, tone: "plain", sub: lead.last_contact_at ? `last ${fmtWhen(lead.last_contact_at, now)}` : "" };
}
